import type {
  AdminExercise,
  AdminLesson,
  AdminLessonsResponse,
  AdminUsersQuery,
  AdminUsersResponse,
  UpdateUserRoleRequest,
  UpdateUserRoleResponse,
  ActivityQuery,
  ActivityResponse,
  AdminAlertsResponse,
  ExercisePrompt,
  CreateLessonRequest,
  DeleteExerciseResponse,
  DeleteLessonResponse,
  ExerciseTypeDescriptor,
  ErrorEnvelope,
  ExportResponse,
  FollowGraphResponse,
  UpdateLessonRequest,
  BlockedLearner,
  ProfileResponse,
  RankingResponse,
  RankingScope,
  SettledWeek,
  LearningSessionResponse,
  LessonReviewResponse,
  LessonsResponse,
  MeResponse,
  PracticeDaysResponse,
  SignInResponse,
  SubmitAttemptRequest,
  SubmitAttemptResponse,
  SubscriptionResponse,
  PaidPlan,
  UpdateMeRequest,
} from './types.js';

/**
 * Fired whenever the set of frozen administrators may have changed — a write
 * came back `423 admin_frozen`, or somebody lifted a freeze — so the banner
 * on every admin screen re-reads it rather than waiting for the next page.
 */
export const ADMIN_ALERTS_CHANGED = 'cavaquinho:admin-alerts-changed';

const BASE_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:7811/api';

/**
 * A failed request, carrying the status and the server's error envelope.
 *
 * The status is preserved rather than collapsed into a message because the
 * practice loop branches on it: 409 on submit means "already submitted, this
 * is the original result" and is a success path, not an error (FR-055 and the
 * spec's double-click edge case).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly envelope: ErrorEnvelope | null,
    readonly body: unknown,
  ) {
    super(envelope?.message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.envelope?.error ?? 'request_failed';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Statuses to return rather than throw — e.g. the 409 submit path. */
  accept?: readonly number[];
}

export interface ApiResult<T> {
  status: number;
  data: T;
}

/**
 * `credentials: 'include'` on every call: authentication is the Better Auth
 * session cookie, and the API names this origin explicitly in its CORS
 * configuration (research D-004).
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { method = 'GET', body, signal, accept = [] } = options;

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers:
      body === undefined
        ? { Accept: 'application/json' }
        : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const payload = await readBody(response);

  if (!response.ok && !accept.includes(response.status)) {
    // A frozen administrator's write: every admin screen's banner re-reads,
    // so the refusal is explained wherever it happened (FR-084).
    if (response.status === 423) window.dispatchEvent(new Event(ADMIN_ALERTS_CHANGED));
    throw new ApiError(response.status, asEnvelope(payload), payload);
  }

  return { status: response.status, data: payload as T };
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function asEnvelope(payload: unknown): ErrorEnvelope | null {
  if (payload && typeof payload === 'object' && 'error' in payload && 'message' in payload) {
    return payload as ErrorEnvelope;
  }
  return null;
}

export const api = {
  /**
   * Better Auth's OAuth entry (`/api/auth/sign-in/social`), not `/api/learn/*`
   * or `/api/me` — it is mounted by Better Auth itself rather than this
   * application's own controllers (`apps/api/src/auth/better-auth.ts`). The
   * caller redirects the browser to the returned `url`; there is no session
   * to read from this response, since the cookie Better Auth sets arrives on
   * the callback it names, not here.
   */
  signInWithGoogle: (callbackURL: string) =>
    request<SignInResponse>('/auth/sign-in/social', {
      method: 'POST',
      body: { provider: 'google', callbackURL },
    }).then((r) => r.data),

  /**
   * Ends the session — Better Auth's own `/api/auth/sign-out`, the other half
   * of `signInWithGoogle` above and mounted by the same handler.
   *
   * The cookie is HttpOnly, so the browser cannot drop it on its own: the
   * server has to be asked, and what comes back is the expiry that clears it.
   *
   * 401 is accepted rather than thrown, because it is this request SUCCEEDING
   * at what it was asked to do: there was no session to end, and the browser
   * is signed out either way. Reporting "não foi possível sair" to somebody
   * who is already out would be the one failure the screen cannot offer a way
   * past.
   */
  signOut: () =>
    request<unknown>('/auth/sign-out', { method: 'POST', accept: [401] }).then(() => undefined),

  /**
   * Ends EVERY session this account has, on every device — Better Auth's
   * `/api/auth/revoke-sessions`.
   *
   * The browser asking is one of them, so this signs the caller out too. That
   * is the point: the reason somebody reaches for it is a machine they no
   * longer have, and a route that spared the current browser would leave the
   * question "did it work here as well?" unanswered on the one screen they
   * can see.
   *
   * 401 is accepted for the same reason `signOut` accepts it: no session is
   * the outcome being asked for.
   */
  signOutEverywhere: () =>
    request<unknown>('/auth/revoke-sessions', { method: 'POST', accept: [401] }).then(
      () => undefined,
    ),

  // account
  me: () => request<MeResponse>('/me').then((r) => r.data),
  updateMe: (payload: UpdateMeRequest) =>
    request<MeResponse>('/me', { method: 'PATCH', body: payload }).then((r) => r.data),
  exportMyData: () => request<ExportResponse>('/me/export').then((r) => r.data),

  /**
   * Empties the account without ending it: attempts, completions, position,
   * history, streak covers and the follow graph in both directions. The
   * account, its settings and the weeks that already settled stay.
   */
  eraseMyData: () => request<null>('/me/data', { method: 'DELETE' }).then(() => undefined),

  deleteMyAccount: () => request<null>('/me', { method: 'DELETE' }).then(() => undefined),

  // ---------------------------------------------------------------- the plan

  subscription: () => request<SubscriptionResponse>('/me/subscription').then((r) => r.data),

  /**
   * Starts a plan. There is no payment processor in the product yet, so this
   * is the whole of subscribing; when a gateway arrives it owns the charge
   * and this call keeps its shape.
   */
  startSubscription: (plan: PaidPlan) =>
    request<SubscriptionResponse>('/me/subscription', { method: 'POST', body: { plan } }).then(
      (r) => r.data,
    ),

  /**
   * Cancels at the end of the period already paid for — the access bought is
   * not taken away the moment somebody stops paying, and `reactivate` puts it
   * back while that period is open.
   */
  cancelSubscription: () =>
    request<SubscriptionResponse>('/me/subscription', { method: 'DELETE' }).then((r) => r.data),

  reactivateSubscription: () =>
    request<SubscriptionResponse>('/me/subscription/reactivate', { method: 'POST' }).then(
      (r) => r.data,
    ),

  // content
  lessons: () => request<LessonsResponse>('/learn/lessons').then((r) => r.data),

  // learning session
  startOrResumeSession: () =>
    request<LearningSessionResponse>('/learn/session', { method: 'POST' }).then((r) => r.data),
  currentSession: () => request<LearningSessionResponse>('/learn/session').then((r) => r.data),

  // practice loop
  presentExercise: (exerciseId: string) =>
    request<Omit<ExercisePrompt, 'promptId'>>(`/learn/exercises/${exerciseId}/prompt`).then(
      (r): ExercisePrompt => ({ ...r.data, promptId: crypto.randomUUID() }),
    ),

  /**
   * 409 is accepted rather than thrown: the contract says the body is the
   * original, unchanged result, which makes a retry after an uncertain network
   * failure safe and idempotent.
   */
  submitAttempt: (payload: SubmitAttemptRequest) =>
    request<SubmitAttemptResponse>('/learn/attempts', {
      method: 'POST',
      body: payload,
      accept: [409],
    }).then((r) => r.data),

  next: () =>
    request<LearningSessionResponse>('/learn/next', { method: 'POST' }).then((r) => r.data),

  // ------------------------------------------------ going back over a Lesson

  /** What the learner answered in this Lesson, exercise by exercise. */
  lessonReview: (lessonId: string) =>
    request<LessonReviewResponse>(`/learn/lessons/${lessonId}/review`).then((r) => r.data),

  /**
   * Practise a finished Lesson again. Returns the refreshed session, already
   * pointed at the Lesson's first Exercise, so the caller can go straight to
   * `/praticar` rather than asking where the learner is a second time.
   *
   * 409 `lesson_not_complete` is the server refusing to move a cursor
   * backwards through unfinished work (FR-070); it throws like any other
   * failure, and the screen simply says the restart did not happen.
   */
  restartLesson: (lessonId: string) =>
    request<LearningSessionResponse>(`/learn/lessons/${lessonId}/restart`, {
      method: 'POST',
    }).then((r) => r.data),

  // ------------------------------------------------------------- the ledger

  /**
   * The recent calendar days and what was recorded on each.
   *
   * The learner's own zone is SENT rather than inferred from the request: a
   * day boundary is a fact about where the person was sitting, and the server
   * has only an IP address and its own clock to guess with. An unrecognised
   * name is answered in UTC and the response says so.
   */
  practiceDays: (timeZone: string) =>
    request<PracticeDaysResponse>(
      `/learn/practice-days?timeZone=${encodeURIComponent(timeZone)}`,
    ).then((r) => r.data),

  // ------------------------------------------------ following, and the board

  /** The learner's own graph, both directions, with the follow flags filled. */
  follows: () => request<FollowGraphResponse>('/learn/follows').then((r) => r.data),

  /**
   * Follow and unfollow are `PUT`/`DELETE` and idempotent, so a double tap
   * costs nothing and no screen needs a guard of its own.
   *
   * A 404 means the learner is not there to follow — which is also what
   * somebody who has blocked you looks like, deliberately and indistinguishably.
   * A 409 is your own block, and the only one you can undo.
   */
  follow: (userId: string) =>
    request<null>(`/learn/follows/${userId}`, { method: 'PUT' }).then(() => undefined),
  unfollow: (userId: string) =>
    request<null>(`/learn/follows/${userId}`, { method: 'DELETE' }).then(() => undefined),

  blocks: () => request<BlockedLearner[]>('/learn/blocks').then((r) => r.data),
  /** Blocking also severs the follow in both directions. */
  block: (userId: string) =>
    request<null>(`/learn/blocks/${userId}`, { method: 'PUT' }).then(() => undefined),
  /** Unblocking restores nothing: whoever wants to follow again may follow again. */
  unblock: (userId: string) =>
    request<null>(`/learn/blocks/${userId}`, { method: 'DELETE' }).then(() => undefined),

  /**
   * The week in progress. No zone is sent, unlike `practiceDays`: a board is a
   * comparison and is cut once for everyone, and the response says in which
   * zone so the screen can label it rather than assume.
   */
  ranking: (scope: RankingScope) =>
    request<RankingResponse>(`/learn/ranking?scope=${scope}`).then((r) => r.data),

  /** The learner's own settled weeks — where the podium count comes from. */
  rankingWeeks: () => request<SettledWeek[]>('/learn/ranking/weeks').then((r) => r.data),

  /** `undefined` asks for the learner's own, which carries the private fields. */
  profile: (userId?: string) =>
    request<ProfileResponse>(`/learn/profile${userId ? `/${userId}` : ''}`).then((r) => r.data),

  // ---------------------------------------------------------- administration

  /**
   * Every route below is `403` with an empty body for a learner (FR-061,
   * SC-011). The Web app's `/admin` boundary keeps a learner from arriving
   * here at all, but the server is the authority — these calls are safe to
   * make and simply fail if the role is wrong.
   *
   * No method takes or returns an `order` value. Reordering submits the FULL
   * ordered id list, because a "move this to position 3" payload cannot be
   * applied to a scope that changed underneath the client without either
   * guessing or corrupting the ordinals.
   */
  adminLessons: () => request<AdminLessonsResponse>('/admin/lessons').then((r) => r.data),

  adminExerciseTypes: () =>
    request<ExerciseTypeDescriptor[]>('/admin/exercise-types').then((r) => r.data),

  /**
   * One page of accounts, with the filter the toolbar is showing.
   *
   * THE QUERY GOES TO THE SERVER. Paging, searching and sorting are done in
   * SQL (contracts/admin-api.md § Users) — the client never holds the whole
   * table, so this screen costs the same at two accounts and at ten thousand.
   * Unset fields are simply omitted and the server applies its own defaults.
   */
  adminUsers: (query: AdminUsersQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    const search = params.toString();
    return request<AdminUsersResponse>(`/admin/users${search ? `?${search}` : ''}`).then(
      (r) => r.data,
    );
  },

  /** One page of the activity log, newest first (FR-083). */
  adminActivity: (query: ActivityQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    const search = params.toString();
    return request<ActivityResponse>(`/admin/activity${search ? `?${search}` : ''}`).then(
      (r) => r.data,
    );
  },

  /** Frozen administrators, for the banner on every admin screen (FR-084). */
  adminAlerts: () => request<AdminAlertsResponse>('/admin/alerts').then((r) => r.data),

  /**
   * Lifts the brake from another administrator. Like the role change, it
   * answers `428 reauth_required` when the caller's Google sign-in is older
   * than a few minutes.
   */
  unfreezeUser: (id: string) =>
    request<{ unfrozen: boolean }>(`/admin/users/${id}/unfreeze`, { method: 'POST' }).then(
      (r) => r.data,
    ),

  /**
   * Grants the administrator role to another account, or takes it back.
   *
   * The caller's OWN id is refused by the server with `409
   * cannot_change_own_role` — nobody demotes themselves, which is also what
   * keeps the platform from reaching zero administrators. The Usuários screen
   * does not offer the control on the signed-in person's own row, so that
   * answer is a backstop rather than something an administrator meets.
   *
   * `428 reauth_required` when the caller's Google sign-in is older than a
   * few minutes — the screen then sends them through Google again.
   */
  updateUserRole: (id: string, payload: UpdateUserRoleRequest) =>
    request<UpdateUserRoleResponse>(`/admin/users/${id}/role`, {
      method: 'PATCH',
      body: payload,
    }).then((r) => r.data),

  /**
   * Uploads a custom audio clip for a `note_identification` Exercise. This
   * bypasses `request()`: every other call sends JSON, but a file body has
   * to travel as `multipart/form-data`, which `fetch` builds correctly on
   * its own the moment the body is a `FormData` — setting `Content-Type`
   * by hand here would omit the multipart boundary and break the upload.
   */
  uploadAdminAudio: async (file: File): Promise<{ url: string }> => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${BASE_URL}/admin/media/audio`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    const payload = await readBody(response);
    if (!response.ok) throw new ApiError(response.status, asEnvelope(payload), payload);
    return payload as { url: string };
  },

  createLesson: (payload: CreateLessonRequest) =>
    request<AdminLesson>('/admin/lessons', { method: 'POST', body: payload }).then((r) => r.data),

  /**
   * The Lesson's own fields — its title, and whether its Exercises reach a
   * learner in the authored order or shuffled per learner. Send only what
   * changed; an omitted field is left alone, and an empty patch is a 422
   * rather than a 200 that did nothing.
   */
  updateLesson: (id: string, patch: UpdateLessonRequest) =>
    request<AdminLesson>(`/admin/lessons/${id}`, { method: 'PATCH', body: patch }).then(
      (r) => r.data,
    ),

  reorderLessons: (lessonIds: readonly string[]) =>
    request<AdminLessonsResponse>('/admin/lessons/order', {
      method: 'PUT',
      body: { lessonIds },
    }).then((r) => r.data),

  deleteLesson: (id: string) =>
    request<DeleteLessonResponse>(`/admin/lessons/${id}`, { method: 'DELETE' }).then((r) => r.data),

  createExercise: (lessonId: string, exerciseType: string, title: string | null, config: unknown) =>
    request<AdminExercise>(`/admin/lessons/${lessonId}/exercises`, {
      method: 'POST',
      body: { exerciseType, title, config },
    }).then((r) => r.data),

  /** `title` and `config` — `exerciseType` is immutable (contracts/admin-api.md). */
  updateExercise: (id: string, exerciseType: string, title: string | null, config: unknown) =>
    request<AdminExercise>(`/admin/exercises/${id}`, {
      method: 'PATCH',
      body: { exerciseType, title, config },
    }).then((r) => r.data),

  reorderExercises: (lessonId: string, exerciseIds: readonly string[]) =>
    request<AdminLesson>(`/admin/lessons/${lessonId}/exercises/order`, {
      method: 'PUT',
      body: { exerciseIds },
    }).then((r) => r.data),

  deleteExercise: (id: string) =>
    request<DeleteExerciseResponse>(`/admin/exercises/${id}`, { method: 'DELETE' }).then(
      (r) => r.data,
    ),
};
